import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useRef } from 'react';
import useOnClickOutside from 'use-onclickoutside';
import { en } from '../i18n/navbar.en.yml';
import useDesktopNavbarManager from './use-desktop-navbar-manager';
import useDrawerManager from './use-drawer-manager';
import useSearchTakeover from './use-search-takeover';

const Drawer = dynamic(() => import('./drawer'), { ssr: false });
const SearchTakeover = dynamic(() => import('./search-takeover'));
const NavbarLeft = dynamic(() => import('./navbar-left'));
const NavbarCenter = dynamic(() => import('./navbar-center'));
const NavbarRight = dynamic(() => import('./navbar-right'));

/**
 * The main navigation bar for the site, which automatically switches between desktop
 * and mobile versions depending on the desktop
 */
function Navbar() {
  const { vt_organic_navbar: i18n } = en;
  const ref = useRef<HTMLDivElement>();

  const { isDrawerLoaded, isDrawerOpen, loadDrawer, openDrawer, closeDrawer } = useDrawerManager();
  const { toggleBlade, resetActiveBlade, currentBlade } = useDesktopNavbarManager();
  const { checkSearchTakeover, showSearchTakeover, searchRef } = useSearchTakeover();

  const handleSearchFocus = () => {
    resetActiveBlade();
    checkSearchTakeover();
  };

  useOnClickOutside(ref, resetActiveBlade);

  return (
    <>
      <nav className="top-0 left-0 right-0 z-10 bg-white shadow dark:bg-[#153154] dark:text-white">
        <div className="p-4 md:px-6">
          {showSearchTakeover ? (
            <SearchTakeover i18n={i18n} onClose={checkSearchTakeover} />
          ) : (
            <div
              className={clsx(
                'grid items-center gap-4 grid-cols-[minmax(auto,1fr)_auto_minmax(max-content,1fr)]',
                '2xl:max-w-[calc(min(100vw,1680px)-3rem)] mx-auto justify-between w-full xl:py-1 dark:xl:py-0 dark:container-xl'
              )}
              ref={ref}
            >
              <NavbarLeft onClickHamburger={openDrawer} onTouchingHamburger={loadDrawer} />
              <NavbarCenter currentBlade={currentBlade} i18n={i18n} toggleBlade={toggleBlade} />
              <NavbarRight
                currentBlade={currentBlade}
                i18n={i18n}
                onSearchFocus={handleSearchFocus}
                searchRef={searchRef}
                toggleBlade={toggleBlade}
              />
            </div>
          )}
        </div>
      </nav>

      {isDrawerLoaded && <Drawer i18n={i18n} isOpen={isDrawerOpen} onCloseDrawer={closeDrawer} />}
    </>
  );
}

export default Navbar;
